import React from 'react';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import EnhancedTableHead from './EnhancedTableHead';
import ReceptionsTableLoader from './ReceptionsTableLoader';
import ColoredLinearProgress from '../../../utils/coloredLinearProgress/ColoredLinearProgress';

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3)
  },
  table: {
    minWidth: 1020
  },
  tableWrapper: {
    overflowX: 'auto'
  }
});

/**
 * Tableau d'affichage de toutes les receptions de marchandises
 *
 * @param {Object} classes                        classes style
 * @param {{Object[]}} receptions                 Liste des receptions reçu du serveur
 * @param {Boolean} loading                       Indique si les requêtes sont terminer
 */
const EnhancedTable = ({
  classes,
  receptions,
  numberOfReceptions,
  loading
}) => {
  const history = useHistory();
  const { search } = useLocation();

  const handleChangePage = (event, currentPage) => {
    history.replace({
      search: qs.stringify({
        ...qs.parse(search, { ignoreQueryPrefix: true }),
        page: +currentPage + 1
      })
    });
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} aria-labelledby="tableTitle">
          <EnhancedTableHead rowCount={numberOfReceptions} loading={loading} />
          {loading ? (
            <ReceptionsTableLoader />
          ) : (
            <TableBody>
              {receptions.map(reception => (
                <TableRow
                  hover
                  style={{ cursor: 'pointer' }}
                  tabIndex={-1}
                  key={reception.id}
                  onClick={() => history.push(`/reception/${reception.id}`)}
                >
                  <TableCell align="left">{reception.reference}</TableCell>
                  <TableCell align="left">{reception.description}</TableCell>
                  <TableCell align="left">{reception.carrierName}</TableCell>
                  <TableCell align="left">
                    {reception.actualDeliveryDate
                      ? moment(reception.actualDeliveryDate).format(
                          'DD/MM/YYYY'
                        )
                      : `prévue le ${moment(
                          reception.expectedDeliveryDate
                        ).format('DD/MM/YYYY')}`}
                  </TableCell>
                  <TableCell align="left">
                    {reception.expectedNumberOfProducts === 0 ? (
                      0
                    ) : (
                      <ColoredLinearProgress
                        showLabel
                        variant="determinate"
                        value={Math.round(
                          (reception.actualNumberOfProducts /
                            reception.expectedNumberOfProducts) *
                            100
                        )}
                      />
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {`Marchandise ${reception.state.name.toLowerCase()}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </div>
      <TablePagination
        disabled
        rowsPerPageOptions={[10]}
        component="div"
        count={numberOfReceptions || 10}
        rowsPerPage={8}
        page={+qs.parse(search, { ignoreQueryPrefix: true }).page - 1}
        backIconButtonProps={{
          disabled:
            loading ||
            +qs.parse(search, { ignoreQueryPrefix: true }).page === 1,
          'aria-label': 'Previous Page'
        }}
        nextIconButtonProps={{
          disabled:
            loading ||
            +qs.parse(search, { ignoreQueryPrefix: true }).page >
              numberOfReceptions / 8,
          'aria-label': 'Next Page'
        }}
        onChangePage={handleChangePage}
      />
    </Paper>
  );
};

const mapStateToProps = state => ({
  receptions: state.receptions.currentReceptions.data,
  numberOfReceptions: state.receptions.currentReceptions.numberOfReceptions,
  loading: state.receptions.loading
});

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired,
  receptions: PropTypes.array.isRequired,
  numberOfReceptions: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired
};

export default connect(mapStateToProps)(withStyles(styles)(EnhancedTable));
